/**
 * ============================================================================
 * DOCUMENTS STORE - CREATE OPERATIONS
 * ============================================================================
 * 
 * Handles creating new documents via the backend API.
 * 
 * @module documents/api/create
 */

import { apiService } from "@/lib/api-functions/api-service";
import { documentStore } from "../store";
import type { DocumentItem, CreateDocumentData, ToastFunction } from "../types";

// ============================================================================
// CREATE OPERATIONS
// ============================================================================

/**
 * Create a new document via API and sync the store.
 * 
 * @param item - The document data to create
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the created document
 * 
 * @example
 * ```ts
 * const newDocument = await createDocumentItem({
 *   game_id: 'game-123',
 *   name: 'Design Document',
 *   data: [{ blocks: [], lastSaved: new Date().toISOString(), widgetData: {} }],
 *   tags: ['design'],
 *   status: 'draft',
 * });
 * ```
 */
export async function createDocumentItem(
  item: CreateDocumentData,
  toast?: ToastFunction
): Promise<DocumentItem> {
  try {
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create via API
    const result = await apiService.createDocumentItem(item);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create document');
    }

    const newItem = result.data;

    // Add the new item to the store (immutably)
    const currentData = documentStore.getData();
    const updatedData = [...currentData, newItem];
    documentStore.setData(updatedData);

    console.log(`[createDocumentItem] Created document: ${newItem.id} - ${newItem.name}`);
    return newItem;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create document';
    console.error('[createDocumentItem] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
